/**
 * TimeSlotSegment - Groups time slots by time of day
 * Displays a labeled section (Morning, Afternoon, Evening) with a grid of time slots
 */

import { TimeSlotButton } from './TimeSlotButton';
import {
  SunMoonIcon,
  SunriseIcon,
  SunIcon,
  MoonIcon,
} from '@butter/ui/icons';
import type { TimeSlotSegmentProps } from './types';

// Map segment IDs to their corresponding icons
const SEGMENT_ICONS = {
  'early-morning': SunMoonIcon,
  'morning': SunriseIcon,
  'afternoon': SunIcon,
  'evening': MoonIcon,
};

export function TimeSlotSegment({
  segment,
  slots,
  slotState,
  onSlotClick,
}: TimeSlotSegmentProps) {
  // Filter slots that belong to this segment
  const segmentSlots = slots.filter(
    (slot) =>
      slot.startMinutes >= segment.startMinutes &&
      slot.startMinutes <= segment.endMinutes
  );

  // Don't render if no slots in this segment
  if (segmentSlots.length === 0) {
    return null;
  }

  // Get the icon for this segment
  const IconComponent = SEGMENT_ICONS[segment.id];

  return (
    <section
      className="space-y-4"
      aria-labelledby={`segment-${segment.id}`}
    >
      {/* Segment header with icon */}
      <h3
        id={`segment-${segment.id}`}
        className="flex items-center gap-2 text-sm font-semibold text-fg-quaternary"
      >
        {IconComponent && (
          <IconComponent
            className="h-5 w-5"
            aria-hidden="true"
          />
        )}
        {segment.label}
      </h3>

      {/* Time slot grid */}
      <div
        className="grid grid-cols-3 gap-2 sm:gap-3 md:grid-cols-4 lg:grid-cols-5"
        role="group"
        aria-label={`${segment.label} time slots`}
        aria-multiselectable="true"
      >
        {segmentSlots.map((slot) => (
          <TimeSlotButton
            key={slot.startMinutes}
            slot={slot}
            state={slotState(slot)}
            onClick={() => onSlotClick(slot)}
          />
        ))}
      </div>
    </section>
  );
}
